import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface NavigationItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  path?: string;
  parent?: string;
}

interface NavigationContextType {
  currentPath: NavigationItem[];
  setNavigationContext: (items: NavigationItem[], currentId: string) => void;
  addNavigationItem: (item: NavigationItem) => void;
  clearNavigation: () => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

interface NavigationProviderProps {
  children: ReactNode;
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
  const [, setNavigationItems] = useState<NavigationItem[]>([]);
  const [currentPath, setCurrentPath] = useState<NavigationItem[]>([]);

  const setNavigationContext = useCallback((items: NavigationItem[], currentId: string) => {
    setNavigationItems(items);
    
    // Build path from root to current item
    const buildPath = (targetId: string, items: NavigationItem[]): NavigationItem[] => {
      const item = items.find(i => i.id === targetId);
      if (!item) return [];
      
      if (item.parent) {
        const parentPath = buildPath(item.parent, items);
        return [...parentPath, item];
      }
      
      return [item];
    };
    
    const path = buildPath(currentId, items);
    setCurrentPath(path);
  }, []);

  const addNavigationItem = useCallback((item: NavigationItem) => {
    setNavigationItems(prev => {
      const exists = prev.find(i => i.id === item.id);
      if (exists) {
        return prev.map(i => i.id === item.id ? item : i);
      }
      return [...prev, item];
    });
  }, []);

  const clearNavigation = useCallback(() => {
    setNavigationItems([]);
    setCurrentPath([]);
  }, []);

  const value: NavigationContextType = {
    currentPath,
    setNavigationContext,
    addNavigationItem,
    clearNavigation,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};